import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { INSTAGRAM_USERNAME_URL } from "schemas/social";

import {
  isValidUsername,
  sanitizeInput,
} from "features/Auth/Onboarding/AboutForm/helpers";

interface InstagramEditDialogProps {
  open: boolean;
  onClose: () => void;
  currentUsername: string;
  onSave: (username: string) => Promise<void>;
}

export default function InstagramEditDialog({
  open,
  onClose,
  currentUsername,
  onSave,
}: InstagramEditDialogProps) {
  const [username, setUsername] = useState(currentUsername);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedInput = sanitizeInput(value.trim());
    setUsername(sanitizedInput);

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setValidationError(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setValidationError(undefined);
    }
  };

  const handleSave = async () => {
    if (!username || validationError || username === currentUsername) {
      return;
    }

    setLoading(true);
    try {
      await onSave(username);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUsername(currentUsername);
    setValidationError(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Instagram Account</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder="handle"
          value={username}
          onChange={handleUsernameChange}
          error={!!validationError}
          helperText={validationError}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{ fontSize: "0.875rem", color: "text.secondary" }}
              >
                {INSTAGRAM_USERNAME_URL}
              </InputAdornment>
            ),
          }}
          sx={{
            mt: 2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSave}
          variant="contained"
          loading={loading}
          disabled={
            !username || !!validationError || username === currentUsername
          }
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
